import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GraduationCap, Loader2, Search, ShieldOff, Star, Users } from "lucide-react";
import { api } from "@/lib/api";
import CustomerDetail from "@/components/admin/CustomerDetail";
import NewCustomerForm from "@/components/admin/NewCustomerForm";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const { customerId } = useParams();
  const navigate = useNavigate();

  const load = (q = search) => api.get("/admin/customers", { params: { search: q } })
    .then(({ data }) => setCustomers(data.customers)).catch(() => setCustomers([]));

  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  if (customerId) return <CustomerDetail customerId={customerId} onBack={() => { navigate("/admin/clients"); load(); }} />;

  return (
    <div data-testid="admin-customers" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-serif text-3xl font-semibold text-bark"><Users size={26} className="text-amber-deep" /> Comptes clients</h1>
          <p className="mt-1 text-sm text-bark-soft">Clients de la boutique et parents d'élèves — un seul compte pour les deux.</p>
        </div>
        <NewCustomerForm onCreated={() => load()} />
        <div className="relative w-full max-w-xs">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-mute" />
          <input data-testid="customers-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, e-mail, téléphone, ville…" className="w-full rounded-full border border-amber-900/20 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-amber-deep" />
        </div>
      </div>

      {!customers ? (
        <p className="flex items-center justify-center gap-2 py-16 text-bark-soft"><Loader2 className="animate-spin text-amber-deep" size={20} /> Chargement…</p>
      ) : customers.length === 0 ? (
        <p data-testid="customers-empty" className="py-16 text-center font-serif text-xl text-bark-soft">Aucun client trouvé.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-amber-800/15 bg-white shadow-sm">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-amber-900/10 text-xs uppercase tracking-wider text-stone-mute">
                <th className="px-5 py-3">Client</th><th className="px-5 py-3">Inscription</th><th className="px-5 py-3">Commandes</th><th className="px-5 py-3">École</th><th className="px-5 py-3">Points</th><th className="px-5 py-3">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-900/10">
              {customers.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => navigate(`/admin/clients/${c.id}`)} className="cursor-pointer transition-colors hover:bg-cream">
                  <td className="px-5 py-3"><p className="font-medium text-bark">{c.name || "—"}</p><p className="text-xs text-bark-soft">{c.email}</p></td>
                  <td className="px-5 py-3 text-bark-soft">{c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-5 py-3 text-bark-soft">{c.shop_orders} boutique · {c.school_orders} école</td>
                  <td className="px-5 py-3 text-bark-soft">{c.school_name ? <span className="inline-flex items-center gap-1"><GraduationCap size={13} className="text-amber-deep" />{c.school_name}</span> : "—"}</td>
                  <td className="px-5 py-3"><span className="inline-flex items-center gap-1 font-semibold text-amber-deep"><Star size={13} className="fill-honey text-honey" />{c.loyalty_points || 0}</span></td>
                  <td className="px-5 py-3">{c.password_set === false && !c.disabled ? <span data-testid={`customer-pending-${c.id}`} className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Fiche · à activer</span> : c.disabled ? <span className="inline-flex items-center gap-1 rounded-full bg-terra/15 px-2.5 py-0.5 text-xs font-medium text-terra"><ShieldOff size={12} /> Désactivé</span> : <span className="rounded-full bg-sage-light px-2.5 py-0.5 text-xs font-medium text-sage">Actif</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
